/**
 * Stripe Configuration
 *
 * Contains publishable key and price IDs for Stripe integration.
 *
 * SETUP INSTRUCTIONS:
 * 1. Create products in Stripe Dashboard (Products > Add Product)
 * 2. Create prices for each product (monthly + yearly)
 * 3. Add your keys to .env file (see .env.example)
 * 4. Never commit .env to git - it's in .gitignore
 *
 * ENVIRONMENT VARIABLES:
 * - VITE_STRIPE_PUBLISHABLE_KEY: Your Stripe publishable key (pk_live_... or pk_test_...)
 * - VITE_STRIPE_PRICE_BASIC_MONTHLY: Price ID for Basic tier monthly
 * - VITE_STRIPE_PRICE_BASIC_YEARLY: Price ID for Basic tier yearly
 * - VITE_STRIPE_PRICE_PRO_MONTHLY: Price ID for Pro tier monthly
 * - VITE_STRIPE_PRICE_PRO_YEARLY: Price ID for Pro tier yearly
 */

// Publishable key from environment (safe to expose in frontend, but managed via env)
// Vite exposes env vars prefixed with VITE_ to the client
// Falls back to empty string if not set (will fail isStripeConfigured check)
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

// Price IDs from environment variables
// These are created when you add pricing to your products in Stripe Dashboard
export const STRIPE_PRICE_IDS = {
  basic: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_BASIC_MONTHLY || '',
    yearly: import.meta.env.VITE_STRIPE_PRICE_BASIC_YEARLY || '',
  },
  pro: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY || '',
    yearly: import.meta.env.VITE_STRIPE_PRICE_PRO_YEARLY || '',
  },
} as const;

// Type for tier keys
export type StripeTier = keyof typeof STRIPE_PRICE_IDS;

/**
 * Get the price ID for a specific tier and billing cycle
 */
export function getPriceId(tier: StripeTier, billingCycle: 'monthly' | 'yearly'): string {
  return STRIPE_PRICE_IDS[tier][billingCycle];
}

/**
 * Check if Stripe is configured (not using placeholder keys)
 */
export function isStripeConfigured(): boolean {
  return (
    !STRIPE_PUBLISHABLE_KEY.includes('REPLACE') &&
    !STRIPE_PRICE_IDS.basic.monthly.includes('REPLACE')
  );
}
